<template>
  <div id="record-invoice-payment-modal">
    <v-layout
      row
      justify-center
    >
      <v-dialog
        v-model="recordPaymentModal"
        persistent
        max-width="500px"
      >
        <v-card>
          <form
            action=""
            @submit.prevent="submitForm()"
          >
            <v-card-title>
              <v-flex
                xs11
                sm11
                md11
              ><span class="headline">{{ $t('recordInvoicePayment.title') }}</span></v-flex>
              <v-flex
                xs1
                sm1
                md1
              >
                <v-btn
                  flat
                  @click="closeRecordPaymentModal(), $validator.reset()"
                  class="close"
                ><span aria-hidden="true">
              <i class="material-icons" style="font-size: 24px;vertical-align: middle;">close</i>
            </span></v-btn>
              </v-flex>
            </v-card-title>
            <v-card-text>
              <v-layout wrap>
                <v-flex
                  xs6
                  class="px-2"
                >
                  <v-text-field
                    :label="$t('recordInvoicePayment.invoiceHashLabel')"
                    :value="activeInvoice.invoice_number"
                    readonly
                  >
                  </v-text-field>
                </v-flex>

                <v-flex
                  xs6
                  class="px-2"
                >
                  <v-text-field
                    :label="$t('columns.remaining')"
                    :value="activeInvoice.remaining_amount"
                    readonly
                  >
                  </v-text-field>
                </v-flex>

                <v-flex
                  v-if="availableBalance > 0"
                  xs12
                  class="px-2"
                >
                  <div class="available-balance-box">
                    <strong>{{ $t('recordInvoicePayment.availableTitle', { amount: '$' + availableBalance.toFixed(2) }) }}</strong>
                    <ul>
                      <li v-for="(s, i) in availableBalanceSources" :key="i">{{ s.label }} ${{ Number(s.amount).toFixed(2) }} ({{ String(s.date).slice(0, 10) }})</li>
                      <li v-if="!availableBalanceSources.length">{{ $t('recordInvoicePayment.unknownSource') }}</li>
                    </ul>
                    <v-btn small outline color="indigo" style="height:auto" @click="useBalance()">{{ $t('recordInvoicePayment.useBalance') }}</v-btn>
                  </div>
                </v-flex>

                <v-flex
                  xs12
                  class="px-2"
                >
                  <v-text-field
                    :label="$t('columns.amount') + '*'"
                    v-model.number="recordPaymentData.amount"
                    :error-messages="errors.collect('amount')"
                    v-validate="'required|decimal:2|min_value:0.01'"
                    data-vv-name="amount"
                  >
                  </v-text-field>
                </v-flex>

                <v-flex
                  xs12
                  class="px-2"
                >
                  <v-select
                    :items="paymentMethods"
                    v-model="recordPaymentData.payment_method"
                    :label="$t('columns.paymentMethod')"
                    item-text="text"
                    item-value="value"
                    clearable
                    single-line
                  >
                  </v-select>
                </v-flex>

                <v-flex
                  xs12
                  class="px-2"
                >
                  <v-text-field
                    :label="$t('columns.note')"
                    v-model="recordPaymentData.note"
                  >
                  </v-text-field>
                </v-flex>

                <v-flex
                  v-if="isPreCreditedMethod"
                  xs12
                  class="px-2"
                >
                  <v-checkbox
                    v-model="recordPaymentData.already_in_credit"
                    :label="$t('recordInvoicePayment.walletBalanceCheckboxLabel')"
                    :disabled="isBalanceMethod"
                    color="indigo"
                    class="wallet-balance-checkbox"
                  ></v-checkbox>
                  <p v-if="isBalanceMethod" class="balance-hint">{{ $t('recordInvoicePayment.balanceHint') }}</p>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                style="height:auto"
                @click="closeRecordPaymentModal(), $validator.reset()"
                outline
                color="normal"
              >{{ $t('common.cancel') }}</v-btn>
              <v-btn
                type="submit"
                :loading="loading"
                :disabled="loading"
                style="height:auto" class="rtl-mr-3pct"
                outline
                color="success"
              >{{ $t('recordInvoicePayment.payButton') }}</v-btn>
            </v-card-actions>
          </form>
        </v-card>
      </v-dialog>
    </v-layout>
  </div>
</template>
<script>
import axios from 'axios'

export default {
  props: [
    'recordPaymentModal',
    'closeRecordPaymentModal',
    'recordPaymentData',
    'activeInvoice',
    'submitRecordPayment',
    'loading'
  ],

  data() {
    return {
      paymentMethods: [
        { text: this.$t('invoice.officeOption'), value: 'office' },
        { text: this.$t('columns.outOffice'), value: 'out_office' },
        { text: 'QI', value: 'qi' },
        { text: 'FIB', value: 'fib' },
        { text: 'FastPay', value: 'fastpay' },
        { text: this.$t('recordInvoicePayment.balanceOption'), value: 'balance' }
      ],
      availableBalance: 0,
      availableBalanceSources: []
    }
  },

  computed: {
    // "Customer already has this in their balance" only actually applies to a FIB/QI wallet
    // top-up that already landed — those are the only methods ever pre-credited to
    // customer.credit before a payment is recorded against an invoice (see the backend's
    // isPreCreditedTopupMethod()). Out Office/Office/Cash/FastPay money is always being
    // collected right now, so the backend now ignores this flag for them and credits the
    // customer for real regardless — hide the checkbox for those methods instead of showing
    // staff a control that quietly does nothing.
    // "Balance" (money already on the wallet for any reason — a refund of a cancelled order, an
    // unused top-up) behaves the same way, with the checkbox always on: it never adds money.
    isPreCreditedMethod() {
      return ['fib', 'qi', 'balance'].includes(this.recordPaymentData.payment_method)
    },
    isBalanceMethod() {
      return this.recordPaymentData.payment_method === 'balance'
    }
  },

  watch: {
    // Switching away from FIB/QI must not leave a stale "already in credit" checked value
    // sitting on the (now hidden) checkbox and submitted anyway.
    isPreCreditedMethod(nowEligible) {
      if (!nowEligible) {
        this.recordPaymentData.already_in_credit = false
      }
    },
    isBalanceMethod(isBalance) {
      if (isBalance) {
        this.recordPaymentData.already_in_credit = true
      }
    },
    // Each time the dialog opens, show the customer's unused balance and where it came from, so
    // staff don't have to remember an earlier refund (CustomerAvailableBalance).
    recordPaymentModal(open) {
      this.availableBalance = 0
      this.availableBalanceSources = []
      const customerId = this.activeInvoice && this.activeInvoice.customer_id_fk
      if (!open || !customerId) {
        return
      }
      axios
        .get(`api/customer/${customerId}/credit-summary`, { headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token') } })
        .then((response) => {
          const data = response.data.data || {}
          this.availableBalance = Number(data.available_balance) || 0
          this.availableBalanceSources = data.available_balance_sources || []
        })
        .catch(() => {})
    }
  },

  methods: {
    useBalance() {
      const remaining = Number(this.activeInvoice.remaining_amount) || 0
      this.recordPaymentData.payment_method = 'balance'
      this.recordPaymentData.already_in_credit = true
      this.recordPaymentData.amount = Number(Math.min(this.availableBalance, remaining).toFixed(2))
    },

    submitForm() {
      this.$validator.validateAll().then(res => {
        if (res) {
          this.submitRecordPayment()
        }
      })
    }
  }
}
</script>
<style scoped>
.available-balance-box {
  border: 1px solid #3f51b5;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: rgba(63, 81, 181, 0.06);
}
.available-balance-box ul {
  margin: 4px 0 6px;
  padding-left: 18px;
}
.balance-hint {
  font-size: 12px;
  color: #3f51b5;
  margin: -8px 0 0 40px;
}
/* Vuetify's default .input-group label is single-line (nowrap + ellipsis)
   and absolutely positioned over the icon, which clips this checkbox's long
   label. Give the icon column a fixed width and let the label wrap next to
   it in normal flow — the icon itself (position/transition) is left
   untouched so its check/uncheck animation still works correctly. */
.wallet-balance-checkbox {
  align-items: center;
}
.wallet-balance-checkbox >>> .input-group__input {
  order: 1;
  flex: 0 0 32px;
  width: 32px;
}
.wallet-balance-checkbox >>> label {
  order: 2;
  position: static;
  display: block;
  flex: 1 1 auto;
  padding-left: 8px;
  height: auto;
  max-width: 100%;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  line-height: 1.3;
}
</style>



// WEBPACK FOOTER //
// src/components/orderInvoice/RecordInvoicePayment.vue